import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useContext, useEffect, useState } from "react";
import PropTypes from 'prop-types';
import DataContext from "../../../../context/DataContext";
import axios from "../../../api/axios";
import ApiRequestErrorHandler from "../../ApiRequestErrorHandler";
import InlineEditModal from "./InlineEditModal";
import InlineEditActions from "./InlineEditActions";

export default function InlineEdit({
  index,
  activeWindow,
  provider,
  trigger,
  setTrigger,
  apiUrl,
  emailTypeOptions,
  handleCancelEdit,
  removeFormFields,
  typeDisplayObj,
  providerTypesActive,
}) {
  const { accessToken, userRoles } = useContext(DataContext);
  const [responseModal, setResponseModal] = useState(false);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [isError, setIsError] = useState(false);
  const [email, setEmail] = useState("");
  const [emailType, setEmailType] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeWindow) {
      setEmail(activeWindow.email);
      setEmailType(activeWindow.email_type);
    }
  }, [activeWindow]);

  const postNewEmail = () => {
    const postData = {
      provider: provider.pk,
      email_type: emailType,
      email: email,
    };
    axios
      .post(apiUrl, postData, {
        headers: { Authorization: `Token ${accessToken}` },
      })
      .then(() => {
        setResponseModal(true);
        setResponseBreakdown(`Provider Email Added`);
        setIsError(false);
      })
      .catch((error) => {
        let res = error.response.data;
        let errArr = ApiRequestErrorHandler(res);
        setIsError(true);
        setResponseModal(true);
        setResponseBreakdown(errArr);
      });
  };

  const patchExistingEmail = () => {
    const patchData = {
      email_type: emailType,
      email: email,
    };

    axios
      .patch(`${apiUrl}${activeWindow.id}/`, patchData, {
        headers: { Authorization: `Token ${accessToken}` },
      })
      .then(() => {
        setResponseModal(true);
        setResponseBreakdown("Provider Email Updated");
        setIsError(false);
      })
      .catch((error) => {
        let res = error.response.data;
        let errArr = ApiRequestErrorHandler(res);
        setIsError(true);
        setResponseModal(true);
        setResponseBreakdown(errArr);
      });
  };

  const deleteExistingEmail = () => {
    axios
      .delete(`${apiUrl}${activeWindow.id}/`, {
        headers: { Authorization: `Token ${accessToken}` },
      })
      .then(() => {
        setResponseModal(true);
        setResponseBreakdown("Provider Email Deleted");
        setIsError(false);
      })
      .catch((error) => {
        let res = error.response.data;
        let errArr = ApiRequestErrorHandler(res);
        setIsError(true);
        setResponseModal(true);
        setResponseBreakdown(errArr);
      });
  };

  const closeResModal = () => {
    setLoading(false);
    setIsError(false);
    setResponseModal(false);
    setResponseBreakdown("");
    setTrigger(!trigger);
    setConfirmDelete(false);
    handleCancelEdit();
  };

  const closeNewResModal = () => {
    if (isError) {
      setLoading(false);
      setIsError(false);
      setResponseModal(false);
      setResponseBreakdown("");
    } else {
      setLoading(false);
      setIsError(false);
      setResponseModal(false);
      setResponseBreakdown("");
      clearFormValState(index);
      handleCancelEdit();
      setTrigger(!trigger);
    }
  };

  const clearFormValState = (index) => {
    setEmail("");
    setEmailType("");
    removeFormFields(index);
  };

  return (
    <div className="flex items-center w-full my-2">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="flex w-full items-center">
          <FormControl className="md:w-1/2 w-full" size="small">
            <InputLabel id="email-type-label">Email Types</InputLabel>
            <Select
              labelId="email-type-label"
              id="email-type"
              value={emailType}
              label="Email Type"
              onChange={(e) => {
                setEmailType(e.target.value);
              }}
            >
              {emailTypeOptions?.length > 0 &&
                emailTypeOptions
                  .filter(email => 
                      activeWindow?.email_type === providerTypesActive[email.key]
                      || providerTypesActive[email.key] !== email.key)
                    .map((email, idx) => (
                      <MenuItem
                        value={email.key}
                        key={idx}
                      >
                        {email.label}
                      </MenuItem>
                    ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Email"
            value={email}
            className="w-full mx-auto text-black ml-2"
            onChange={(e) => setEmail(e.target.value)}
          />{" "}
        </div>
      </LocalizationProvider>

      <InlineEditActions
        onSave={() => !activeWindow ? postNewEmail() : patchExistingEmail()}
        onDelete={() => setConfirmDelete(true)}
        onCancel={() => !activeWindow ? clearFormValState(index) : handleCancelEdit()}
        loading={loading}
        canAdd={userRoles.permissions.includes("provider.add_provideremail")}
        canPatch={userRoles.permissions.includes("provider.change_provideremail")}
        canRemove={userRoles.permissions.includes("provider.delete_provideremail")}
        activeWindow={activeWindow}
        title="email"
      />

      <InlineEditModal
        isError={isError}
        responseModal={responseModal}
        setResponseModal={setResponseModal}
        responseBreakdown={responseBreakdown}
        confirmDelete={confirmDelete}
        setConfirmDelete={setConfirmDelete}
        activeWindow={activeWindow}
        typeDisplayObj={typeDisplayObj}
        displayKey="email_type"
        displayValue="email"
        onDelete={deleteExistingEmail}
        onClose={closeResModal}
        onNewClose={closeNewResModal}
        loading={loading}
        title="email"
      />
    </div>
  );
}

InlineEdit.propTypes = {
  index: PropTypes.number.isRequired,
  activeWindow: PropTypes.object,
  provider: PropTypes.object.isRequired,
  trigger: PropTypes.bool.isRequired,
  setTrigger: PropTypes.func.isRequired,
  apiUrl: PropTypes.string.isRequired,
  emailTypeOptions: PropTypes.array,
  handleCancelEdit: PropTypes.func.isRequired,
  removeFormFields: PropTypes.func.isRequired,
  typeDisplayObj: PropTypes.object.isRequired,
  providerTypesActive: PropTypes.object.isRequired,
};
